import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useBreadcrumbs } from '../contexts/BreadcrumbsContext';
import AdminUsers from '../components/Admin/AdminUsers';
import AdminUserEdit from '../components/Admin/AdminUserEdit';
import AdminRoles from '../components/Admin/AdminRoles';
import AdminRoleDetail from '../components/Admin/AdminRoleDetail';
import AuditLogPage from '../components/AuditLog/AuditLogPage';
import Header from '../components/shared/TailwindComponents/Header';
import FilterBarWithTabs from '../components/shared/TailwindComponents/FilterBarWithTabs';
import { PiUploadLight } from 'react-icons/pi';

type AdminTabType = 'users' | 'roles' | 'audit-logs';

interface RouteParams {
  userId?: string;
  roleId?: string;
}

const AdminPageContent: React.FC<{ userInfo: any }> = ({ userInfo }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { setBreadcrumbs } = useBreadcrumbs();
  const params = useParams<RouteParams>();

  const [activeTab, setActiveTab] = useState<AdminTabType>(() => {
    const searchParams = new URLSearchParams(location.search);
    return (searchParams.get('tab') as AdminTabType) || 'users';
  });
  const [inviteUserTrigger, setInviteUserTrigger] = useState(false);
  const [importUserTrigger, setImportUserTrigger] = useState(false);

  const handleCloseInviteModal = () => {
    setInviteUserTrigger(false);
  };

  useEffect(() => {
    const breadcrumbs = [
      { name: 'Admin', href: '/admin', current: true },
    ];
    setBreadcrumbs(breadcrumbs);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    history.push(`/admin?tab=${activeTab}`);
  }, [activeTab, history]);

  const tabs = [
    { label: t('Users'), value: 'users' },
    { label: t('Roles'), value: 'roles' },
    { label: t('Audit Logs'), value: 'audit-logs' },
  ];

  const handleTabClick = (tab: string) => {
    setActiveTab(tab as AdminTabType);
  };

  const getHeaderActions = () => {
    switch (activeTab) {
      case 'users':
        return [
          {
            label: t('Import Users'),
            icon: <PiUploadLight className="w-4 h-4" />,
            onClick: () => setImportUserTrigger(true),
            variant: 'outline' as const,
          },
          {
            label: t('Invite User'),
            onClick: () => {
              setInviteUserTrigger(!inviteUserTrigger);
            },
            variant: 'primary' as const,
          },
        ];
      case 'roles':
        return [
          {
            label: t('Create Role'),
            onClick: () => {
              history.push('/admin/roles/create');
            },
            variant: 'primary' as const,
          },
        ];
      default:
        return [];
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return <AdminUsers openInviteModal={inviteUserTrigger} onCloseInvite={handleCloseInviteModal} openImportModal={importUserTrigger} onCloseImport={() => setImportUserTrigger(false)} />;
      case 'roles':
        return <AdminRoles />;
      case 'audit-logs':
        return <AuditLogPage embedded />;
      default:
        return <AdminUsers openImportModal={importUserTrigger} onCloseImport={() => setImportUserTrigger(false)} />;
    }
  };

  return (
    <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
      {inviteUserTrigger && activeTab === 'users' ? (
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      ) : (
        <>
          <Header actions={getHeaderActions()} />
          <FilterBarWithTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={handleTabClick}
          />
          <div className="flex-1 overflow-y-auto">
            {renderContent()}
          </div>
        </>
      )}
    </div>
  );
};

const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const params = useParams<RouteParams>();

  useEffect(() => {
    if (!userInfo?.is_org_admin) {
      history.push('/not-found');
    }
  }, [userInfo?.is_org_admin, history]);

  if (!userInfo?.is_org_admin) {
    return null;
  }

  if (params.userId) {
    return <AdminUserEdit />;
  }

  if (params.roleId) {
    return <AdminRoleDetail />;
  }

  return <AdminPageContent userInfo={userInfo} />;
};

export default AdminPage;
